import React from 'react';
import {
  QUALIFICATION_CHECK_STATUS,
  QUALIFICATION_CHECK_STATUS_TEXT,
} from '../constants';
import type { DepartmentsAndEntryYear } from '../graphql';
import { VISA_STATUS_TEXT } from '../lang';
import {
  COMMON_FILTER_TEXT,
  currentStatus,
  localIndicator,
  programme,
} from '../../Assessment';
import {
  type DropDownSelectOption,
  type OptionGroups,
  FilterPanel,
  type FilterPanelProps,
} from '../../../components';
import { convertEntryYearMap } from '../../Application/components';

type ARFilterProps = Omit<FilterPanelProps, 'filterOptionGroups'> & {
  departmentsAndEntryYear: DepartmentsAndEntryYear;
  showVisa?: boolean;
  isAllQualification?: boolean;
  showQualificationCheck?: boolean;
};

export const ARFilter: React.FC<ARFilterProps> = props => {
  const {
    isAllQualification = true,
    initialAppliedFilter,
    departmentsAndEntryYear,
    setFilter: setParentFilter,
    showQualificationCheck = false,
    showVisa = false,
  } = props;

  const { departments, entryYears } = departmentsAndEntryYear;

  const departmentOptions: DropDownSelectOption[] = (departments as any[]).map(
    department => ({
      value: department.value,
      label: department.label,
    }),
  );

  const entryYearsOptions: DropDownSelectOption[] = (entryYears as any[]).map(
    entryYear => ({
      value: entryYear,
      label: convertEntryYearMap[entryYear] ?? entryYear,
    }),
  );

  const toDoStatus = [
    QUALIFICATION_CHECK_STATUS.CREATED,
    QUALIFICATION_CHECK_STATUS.CHECKING,
    QUALIFICATION_CHECK_STATUS.MODIFICATION_REQUIRED,
  ];

  const qualificationStatusToDoOptions = toDoStatus.map(key => {
    return {
      value: key,
      label:
        QUALIFICATION_CHECK_STATUS_TEXT[
          key as keyof typeof QUALIFICATION_CHECK_STATUS_TEXT
        ],
    };
  });

  const qualificationStatusAllOptions = Object.keys(
    QUALIFICATION_CHECK_STATUS,
  ).map(key => {
    return {
      value: key,
      label:
        QUALIFICATION_CHECK_STATUS_TEXT[
          key as keyof typeof QUALIFICATION_CHECK_STATUS_TEXT
        ],
    };
  });

  const filterOptionGroups: OptionGroups[] = [
    {
      title: COMMON_FILTER_TEXT.FILTER_CATEGORY.DEPT,
      options: departmentOptions,
      type: 'select',
      fieldName: 'department',
      fieldType: 'string',
      displayLabel: false, // to display the abb
    },
    {
      title: COMMON_FILTER_TEXT.FILTER_CATEGORY.ENTRY_YR,
      options: entryYearsOptions,
      type: 'select',
      fieldName: 'entryYear',
      fieldType: 'string',
      displayLabel: false,
    },
    {
      title: COMMON_FILTER_TEXT.FILTER_CATEGORY.CURRENT_STATUS,
      options: currentStatus(false),
      type: 'select',
      fieldName: 'applicationStatus',
      fieldType: 'string',
      displayLabel: true,
    },
    {
      title: COMMON_FILTER_TEXT.FILTER_CATEGORY.ADM_PROG,
      options: programme,
      type: 'select',
      fieldName: 'programme',
      fieldType: 'string',
      displayLabel: true,
    },
    {
      title: COMMON_FILTER_TEXT.FILTER_CATEGORY.LOCAL_INDICATOR,
      options: localIndicator,
      type: 'select',
      fieldName: 'isLocal',
      fieldType: 'boolean',
      displayLabel: true,
    },
    ...(showVisa
      ? ([
          {
            title: COMMON_FILTER_TEXT.FILTER_CATEGORY.VISA_STATUS,
            fieldName: 'visaStatus',
            options: Object.keys(VISA_STATUS_TEXT).map(key => {
              return {
                value: key,
                label: VISA_STATUS_TEXT[key as keyof typeof VISA_STATUS_TEXT],
              };
            }),
            type: 'select',
            displayLabel: true,
          },
        ] as OptionGroups[])
      : []),
    ...(showQualificationCheck
      ? ([
          {
            title:
              COMMON_FILTER_TEXT.FILTER_CATEGORY.QUALIFICATION_CHECK_STATUS,
            fieldName: 'qualificationCheckStatus',
            options: isAllQualification
              ? qualificationStatusAllOptions
              : qualificationStatusToDoOptions,
            type: 'select',
            displayLabel: true,
          },
        ] as OptionGroups[])
      : []),
    {
      title: COMMON_FILTER_TEXT.FILTER_CATEGORY.APPLICATION_NAME,
      fieldName: 'applicantName',
      type: 'text',
      isFuzzy: true,
    },
    {
      title: COMMON_FILTER_TEXT.FILTER_CATEGORY.APPLICATION_NO,
      fieldName: 'applicationNo',
      type: 'text',
      isFuzzy: true,
    },
  ];

  return (
    <>
      <FilterPanel
        setFilter={setParentFilter}
        initialAppliedFilter={initialAppliedFilter}
        filterOptionGroups={filterOptionGroups}
      />
    </>
  );
};
